import type { Agent, AgentStatus } from '@/types/agent';
import type { Room } from '@/types/room';
import { findRoomPath, roomPathToPixels } from './Pathfinder';

/** Durée minimale (en ticks) avant de changer d'état */
const MIN_STATUS_TICKS: Record<AgentStatus, number> = {
  idle: 3,
  working: 5,
  moving: 1,
  thinking: 2,
  waiting_validation: 10,
  offline: 20,
  error: 5,
  meeting: 8,
};

/** Probabilités de transition d'état par tick (quand durée min atteinte) */
type TransitionMap = Partial<Record<AgentStatus, number>>;
const TRANSITIONS: Record<AgentStatus, TransitionMap> = {
  idle: { working: 0.6, moving: 0.2, thinking: 0.1, meeting: 0.05, idle: 0.05 },
  working: { working: 0.5, thinking: 0.15, idle: 0.1, moving: 0.15, waiting_validation: 0.08, meeting: 0.02 },
  thinking: { working: 0.7, idle: 0.2, moving: 0.1 },
  moving: { working: 0.5, idle: 0.3, thinking: 0.2 },
  waiting_validation: { waiting_validation: 0.9, idle: 0.1 },
  meeting: { working: 0.6, idle: 0.3, moving: 0.1 },
  offline: { offline: 1.0 },
  error: { idle: 0.3, working: 0.4, error: 0.3 },
};

/** Salles où un agent peut se rendre selon son rôle */
const ROLE_DESTINATIONS: Record<string, string[]> = {
  director: ['dir', 'mkt', 'seo', 'mtg'],
  cmo: ['mkt', 'mtg', 's1', 's2', 's3', 's4'],
  seo: ['seo', 'mtg', 's1', 's2', 's3', 's4'],
  catalogue: ['cat', 'mtg', 's1', 's2', 's3', 's4'],
  communication: ['com', 'mkt', 'mtg', 's1'],
  sav: ['sav', 'mtg', 's1', 's2', 's3', 's4'],
  data: ['dat', 'mtg', 'dir'],
  marketplace: ['mkp', 'mtg', 's1', 's2', 's3', 's4'],
  qa: ['qa', 's1', 's2', 's3', 's4'],
};

function pickWeighted(map: TransitionMap): AgentStatus {
  const total = Object.values(map).reduce((s, v) => s + (v ?? 0), 0);
  let r = Math.random() * total;
  for (const [status, prob] of Object.entries(map)) {
    r -= prob ?? 0;
    if (r <= 0) return status as AgentStatus;
  }
  return 'idle';
}

function pickDestination(agent: Agent, rooms: Room[]): string {
  const candidates = ROLE_DESTINATIONS[agent.role] ?? [agent.currentRoomId];
  const filtered = candidates.filter(id => id !== agent.currentRoomId);
  const roomIds = rooms.map(r => r.id);
  const valid = filtered.filter(id => roomIds.includes(id));
  if (!valid.length) return agent.currentRoomId;
  return valid[Math.floor(Math.random() * valid.length)];
}

export interface AgentTickResult {
  newStatus?: AgentStatus;
  newRoomId?: string;
  pixelPath?: Array<{ x: number; y: number }>;
  targetRoomId?: string | null;
  needsValidation?: boolean;
}

export function tickAgent(agent: Agent, rooms: Room[]): AgentTickResult {
  const result: AgentTickResult = {};

  // Ignore les agents hors-ligne
  if (!agent.online) return result;

  const minDuration = MIN_STATUS_TICKS[agent.status] ?? 3;
  if (agent.statusDuration < minDuration) return result;

  const transitions = TRANSITIONS[agent.status] ?? { idle: 1 };
  const nextStatus = pickWeighted(transitions);

  if (nextStatus === agent.status && nextStatus !== 'moving') {
    // Reste dans le même état, rien à faire de spécial
    return result;
  }

  result.newStatus = nextStatus;

  if (nextStatus === 'moving') {
    const dest = pickDestination(agent, rooms);
    if (dest !== agent.currentRoomId) {
      const roomPath = findRoomPath(agent.currentRoomId, dest, rooms);
      const pixels = roomPathToPixels(roomPath, rooms, agent.id);
      result.newRoomId = dest;
      result.targetRoomId = dest;
      result.pixelPath = pixels;
    } else {
      result.newStatus = 'idle';
    }
  }

  if (nextStatus === 'waiting_validation' && agent.status !== 'waiting_validation') {
    result.needsValidation = true;
  }

  return result;
}
